import { supabase } from '@/db/supabase';

// --- Community Logic ---

export interface Post {
  id: string;
  user_id: string;
  title: string;
  content: string;
  images: string[];
  tags: string[];
  likes_count: number;
  comments_count: number;
  views_count: number;
  created_at: string;
  profiles?: {
    username: string;
    avatar_url: string;
  };
}

export interface Comment {
  id: string;
  post_id: string;
  user_id: string;
  content: string;
  likes_count: number;
  created_at: string;
  parent_id?: string | null;
  profiles?: {
    username: string;
    avatar_url: string;
  };
}

export const getPosts = async (limit = 20, offset = 0) => {
  const { data, error } = await supabase
    .from('community_posts')
    .select('*, profiles(username, avatar_url)')
    .order('created_at', { ascending: false })
    .range(offset, offset + limit - 1);
  if (error) throw error;
  return data as Post[];
};

export const getTrendingPosts = async () => {
  const { data, error } = await supabase
    .from('trending_posts')
    .select('*, profiles(username, avatar_url)');
  if (error) throw error;
  return data as Post[];
};

export const createPost = async (post: Partial<Post>) => {
  const { data, error } = await supabase
    .from('community_posts')
    .insert(post)
    .select()
    .single();
  if (error) throw error;
  return data as Post;
};

export const getPostComments = async (postId: string) => {
  const { data, error } = await supabase
    .from('community_comments')
    .select('*, profiles(username, avatar_url)')
    .eq('post_id', postId)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return data as Comment[];
};

export const createComment = async (comment: Partial<Comment>) => {
  const { data, error } = await supabase
    .from('community_comments')
    .insert(comment)
    .select()
    .single();
  if (error) throw error;
  return data as Comment;
};

export const toggleLike = async (userId: string, targetId: string, targetType: 'post' | 'comment') => {
  // Check if liked
  const { data: existingLike, error: fetchError } = await supabase
    .from('community_likes')
    .select('*')
    .eq('user_id', userId)
    .eq('target_id', targetId)
    .eq('target_type', targetType)
    .maybeSingle();

  if (fetchError) throw fetchError;

  if (existingLike) {
    // Unlike
    const { error: deleteError } = await supabase
      .from('community_likes')
      .delete()
      .eq('id', existingLike.id);
    if (deleteError) throw deleteError;
    return false;
  } else {
    // Like
    const { error: insertError } = await supabase
      .from('community_likes')
      .insert({ user_id: userId, target_id: targetId, target_type: targetType });
    if (insertError) throw insertError;
    return true;
  }
};

// --- Payment & Membership Logic ---

export interface SKU {
  sku_code: string;
  name: string;
  price: number;
}

export interface Order {
  order_no: string;
  status: 'pending' | 'paid' | 'cancelled' | 'refunded';
  wechat_pay_url: string;
  total_amount: number;
  sku_code: string;
  created_at: string;
}

export const getSKUs = async () => {
  const { data, error } = await supabase
    .from('sku')
    .select('*');
  if (error) throw error;
  return data as SKU[];
};

export const createPaymentOrder = async (skuCode: string) => {
  const { data, error } = await supabase.functions.invoke('create_payment_order', {
    body: { sku_code: skuCode }
  });
  if (error) throw error;
  return data as { order_no: string; wechat_pay_url: string };
};

export const getOrderDetails = async (orderNo: string) => {
  const { data, error } = await supabase
    .from('orders')
    .select('*')
    .eq('order_no', orderNo)
    .single();
  if (error) throw error;
  return data as Order;
};

// --- Badge Shop Logic ---

export interface BadgeShopItem {
  id: string;
  name: string;
  description: string;
  icon: string;
  type: 'badge' | 'avatar_frame' | 'title';
  price: number;
  stock: number;
  is_limited: boolean;
}

export const getBadgeShopItems = async () => {
  const { data, error } = await supabase
    .from('badge_shop')
    .select('*')
    .order('sort_order');
  if (error) throw error;
  return data as BadgeShopItem[];
};

export const getUserBadges = async (userId: string) => {
  const { data, error } = await supabase
    .from('user_badges')
    .select('*, badge_shop(*)')
    .eq('user_id', userId);
  if (error) throw error;
  return data;
};

export const redeemBadge = async (badgeId: string) => {
  const { data, error } = await supabase.rpc('redeem_badge', { badge_id_param: badgeId });
  if (error) throw error;
  return data;
};

// --- Video Clips Logic ---

export interface VideoClip {
  id: string;
  user_id: string;
  original_tutorial_id: string;
  clip_url: string;
  start_time: number;
  end_time: number;
  duration: number;
  title: string;
  description: string;
  is_shared_to_community: boolean;
  created_at: string;
}

export const createVideoClip = async (clipData: Partial<VideoClip>) => {
  const { data, error } = await supabase
    .from('video_clips')
    .insert(clipData)
    .select()
    .single();
  if (error) throw error;
  return data as VideoClip;
};

export const getUserVideoClips = async (userId: string) => {
  const { data, error } = await supabase
    .from('video_clips')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data as VideoClip[];
};

// --- AI Stats Logic ---

export interface AILog {
  id: string;
  user_id: string;
  provider: string;
  model: string;
  prompt_tokens: number;
  completion_tokens: number;
  total_tokens: number;
  cost_usd: number;
  endpoint: string;
  status: string;
  created_at: string;
}

export const getAILogs = async (startDate: Date) => {
  const { data, error } = await supabase
    .from('ai_api_logs')
    .select('*')
    .gte('created_at', startDate.toISOString())
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data as AILog[];
};

// --- Daily Tasks Logic ---

export interface DailyTask {
  id: string;
  task_key: string;
  name: string;
  description: string;
  icon: string;
  reward_points: number;
  max_daily_count: number;
  is_enabled: boolean;
}

export const getDailyTasks = async () => {
  const { data, error } = await supabase
    .from('daily_tasks')
    .select('*')
    .eq('is_enabled', true)
    .order('sort_order');
  if (error) throw error;
  return data as DailyTask[];
};

export const getUserTaskProgress = async (userId: string, date: string) => {
  const { data, error } = await supabase
    .from('user_task_progress')
    .select('*')
    .eq('user_id', userId)
    .eq('progress_date', date);
  if (error) throw error;
  return data;
};

export const checkIn = async () => {
  const { data, error } = await supabase.rpc('check_in');
  if (error) throw error;
  return data;
};

export const completeTask = async (taskKey: string) => {
  const { data, error } = await supabase.rpc('complete_task', { task_key_param: taskKey });
  if (error) throw error;
  return data;
};

// --- Video Templates Logic ---

export interface VideoTemplate {
  id: string;
  name: string;
  description: string;
  category: 'opening' | 'transition' | 'subtitle' | 'music';
  thumbnail_url: string;
  config: any;
  is_premium: boolean;
}

export const getVideoTemplates = async () => {
  const { data, error } = await supabase
    .from('video_templates')
    .select('*')
    .order('sort_order');
  if (error) throw error;
  return data as VideoTemplate[];
};

// --- Cost Alert Rules Logic ---

export interface CostAlertRule {
  id: string;
  provider: string | null;
  alert_type: 'daily' | 'monthly';
  threshold_usd: number;
  is_enabled: boolean;
  last_triggered_at: string | null;
}

export const getCostAlertRules = async () => {
  const { data, error } = await supabase
    .from('cost_alert_rules')
    .select('*')
    .order('created_at');
  if (error) throw error;
  return data as CostAlertRule[];
};

export const updateCostAlertRule = async (ruleId: string, updates: Partial<CostAlertRule>) => {
  const { data, error } = await supabase
    .from('cost_alert_rules')
    .update(updates)
    .eq('id', ruleId)
    .select()
    .single();
  if (error) throw error;
  return data;
};

// --- Achievements Logic ---

export interface Achievement {
  id: string;
  achievement_key: string;
  name: string;
  description: string;
  icon: string;
  category: string;
  requirement_type: string;
  requirement_value: number;
  reward_points: number;
  is_hidden: boolean;
}

export interface UserAchievement {
  id: string;
  user_id: string;
  achievement_id: string;
  progress: number;
  is_unlocked: boolean;
  unlocked_at: string;
}

export const getAchievements = async () => {
  const { data, error } = await supabase
    .from('achievements')
    .select('*')
    .order('sort_order');
  if (error) throw error;
  return data as Achievement[];
};

export const getUserAchievements = async (userId: string) => {
  const { data, error } = await supabase
    .from('user_achievements')
    .select('*, achievements(*)')
    .eq('user_id', userId);
  if (error) throw error;
  return data;
};

export const checkAchievementProgress = async (userId: string) => {
  const { data, error } = await supabase.rpc('check_achievement_progress', { user_id_param: userId });
  if (error) throw error;
  return data;
};

// --- Template Marketplace Logic ---

export interface MarketplaceTemplate {
  id: string;
  creator_id: string;
  name: string;
  description: string;
  category: string;
  thumbnail_url: string;
  config: any;
  price_points: number;
  download_count: number;
  rating_avg: number;
  revenue_total: number;
  status: string;
}

export const getMarketplaceTemplates = async () => {
  const { data, error } = await supabase
    .from('template_marketplace')
    .select('*, profiles(username)')
    .eq('status', 'approved')
    .order('download_count', { ascending: false });
  if (error) throw error;
  return data as MarketplaceTemplate[];
};

export const getMyTemplates = async (userId: string) => {
  const { data, error } = await supabase
    .from('template_marketplace')
    .select('*')
    .eq('creator_id', userId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data as MarketplaceTemplate[];
};

export const createMarketplaceTemplate = async (templateData: Partial<MarketplaceTemplate>) => {
  const { data, error } = await supabase
    .from('template_marketplace')
    .insert(templateData)
    .select()
    .single();
  if (error) throw error;
  return data;
};

export const purchaseTemplate = async (templateId: string) => {
  const { data, error } = await supabase.rpc('purchase_template', { template_id_param: templateId });
  if (error) throw error;
  return data;
};

export const submitTemplateReview = async (templateId: string, rating: number, reviewText: string, tags: string[]) => {
  const { data, error } = await supabase.rpc('submit_template_review', {
    template_id_param: templateId,
    rating_param: rating,
    review_text_param: reviewText,
    tags_param: tags,
  });
  if (error) throw error;
  return data;
};

export const getTemplateReviews = async (templateId: string) => {
  const { data, error } = await supabase
    .from('template_reviews')
    .select('*, profiles(username, avatar_url)')
    .eq('template_id', templateId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data;
};

// --- Auto Optimization Logic ---

export interface AutoOptimizationConfig {
  id: string;
  is_enabled: boolean;
  strategy: string;
  min_success_rate: number;
  current_provider: string;
  last_switched_at: string;
}

export const getAutoOptimizationConfig = async () => {
  const { data, error } = await supabase
    .from('auto_optimization_config')
    .select('*')
    .single();
  if (error) throw error;
  return data as AutoOptimizationConfig;
};

export const updateAutoOptimizationConfig = async (updates: Partial<AutoOptimizationConfig>) => {
  const { data, error } = await supabase
    .from('auto_optimization_config')
    .update(updates)
    .eq('id', updates.id)
    .select()
    .single();
  if (error) throw error;
  return data;
};

export const getOptimizationReports = async (limit = 7) => {
  const { data, error } = await supabase
    .from('optimization_reports')
    .select('*')
    .order('report_date', { ascending: false })
    .limit(limit);
  if (error) throw error;
  return data;
};

export const getOptimalProvider = async () => {
  const { data, error } = await supabase.rpc('get_optimal_provider');
  if (error) throw error;
  return data;
};

// --- Review Likes Logic ---

export const toggleReviewLike = async (reviewId: string) => {
  const { data, error } = await supabase.rpc('toggle_review_like', { review_id_param: reviewId });
  if (error) throw error;
  return data;
};

export const getReviewLikes = async (reviewIds: string[]) => {
  const { data, error } = await supabase
    .from('review_likes')
    .select('review_id')
    .in('review_id', reviewIds);
  if (error) throw error;
  return data;
};

// --- User Preferences Logic ---

export interface UserPreferences {
  id: string;
  user_id: string;
  language: string;
  theme: string;
  notification_enabled: boolean;
  email_notification: boolean;
}

export const getUserPreferences = async () => {
  const { data, error } = await supabase.rpc('get_or_create_user_preferences');
  if (error) throw error;
  return data as UserPreferences;
};

export const updateUserPreferences = async (updates: Partial<UserPreferences>) => {
  const { data, error } = await supabase.rpc('update_user_preferences', {
    language_param: updates.language || null,
    theme_param: updates.theme || null,
    notification_enabled_param: updates.notification_enabled ?? null,
    email_notification_param: updates.email_notification ?? null,
  });
  if (error) throw error;
  return data;
};
